import { parseIdpApiDate } from 'pages/Shoppers/utils/idpApiDate';

/**
 * Revive (CRM lead) ids the user has seen. The key predates the Revive/Reveal naming fix but has
 * always held CRM lead ids, so it is kept to preserve existing users' seen state.
 */
const seenReviveIdsKey = (dealerId: number, userId: number) =>
  `turn4:idp-notification-seen:${dealerId}:${userId}`;
/** Last `revealSeenDate` (yyyyMMdd) this browser sent — drives the Reveal rows' unread dots. */
const revealSeenDateKey = (dealerId: number, userId: number) =>
  `turn4:idp-notification-reveal-seen-date:${dealerId}:${userId}`;

function parseStringArray(raw: string | null): string[] | null {
  if (raw == null || raw === '') return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((x) => typeof x === 'string') ? parsed : null;
  } catch {
    return null;
  }
}

function readItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore — storage can be full or blocked; seen state then falls back to the API
  }
}

export function readSeenReviveIdsFromLocalStorage(
  dealerId: number,
  userId: number,
): string[] | null {
  return parseStringArray(readItem(seenReviveIdsKey(dealerId, userId)));
}

export function persistSeenReviveIds(dealerId: number, userId: number, seen: string[]): void {
  writeItem(seenReviveIdsKey(dealerId, userId), JSON.stringify(seen));
}

export function markReviveIdsSeenInLocalStorage(
  dealerId: number | undefined,
  userId: number | undefined,
  reviveIds: string[],
): void {
  if (dealerId == null || userId == null || reviveIds.length === 0) return;

  const prev = readSeenReviveIdsFromLocalStorage(dealerId, userId) ?? [];
  persistSeenReviveIds(dealerId, userId, [...new Set([...prev, ...reviveIds])]);
}

export function readRevealSeenDateFromLocalStorage(
  dealerId: number,
  userId: number,
): string | null {
  const raw = readItem(revealSeenDateKey(dealerId, userId));
  return parseIdpApiDate(raw) ? raw : null;
}

/** Stores `revealSeenDate`, never moving it backwards — matching the API, which ignores older dates. */
export function persistRevealSeenDate(
  dealerId: number | undefined,
  userId: number | undefined,
  revealSeenDate: string,
): void {
  if (dealerId == null || userId == null || !parseIdpApiDate(revealSeenDate)) return;

  const prev = readRevealSeenDateFromLocalStorage(dealerId, userId);
  // yyyyMMdd strings compare chronologically.
  if (prev && prev >= revealSeenDate) return;
  writeItem(revealSeenDateKey(dealerId, userId), revealSeenDate);
}
